import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Dashboard.css';

const fmtINR = v => v >= 100000 ? '₹' + (v/100000).toFixed(1) + 'L' : v >= 1000 ? '₹' + (v/1000).toFixed(0) + 'K' : '₹' + v;

function Spark({ data, color }) {
  const max = Math.max(...data), min = Math.min(...data);
  const W = 80, H = 28, P = 2;
  const pts = data.map((v,i) => ({
    x: P + (i/(data.length-1))*(W-P*2),
    y: P + (1-(v-min)/(max-min||1))*(H-P*2)
  }));
  const d = pts.map((p,i) => `${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:W,height:H,display:'block'}}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/>
    </svg>
  );
}

function RevenueChart({ data }) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun'];
  const max = Math.max(...data);
  const W = 520, H = 120, PL = 8, PB = 22, PT = 12, PR = 8;
  const pts = data.map((v,i) => ({
    x: PL + (i/(data.length-1))*(W-PL-PR),
    y: PT + (1-v/max)*(H-PT-PB)
  }));
  const pathD = pts.map((p,i) => `${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
  const areaD = `${pathD} L${pts[pts.length-1].x},${H-PB} L${pts[0].x},${H-PB} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H,display:'block'}}>
      <defs>
        <linearGradient id="dbRevGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F5A623" stopOpacity="0.28"/>
          <stop offset="100%" stopColor="#F5A623" stopOpacity="0"/>
        </linearGradient>
      </defs>
      {[0,1,2,3].map(i => {
        const y = PT + i*(H-PT-PB)/3;
        const val = Math.round(max - (max/3)*i);
        return <line key={i} x1={PL} y1={y} x2={W-PR} y2={y} stroke="currentColor" strokeOpacity="0.06" strokeWidth="0.5"/>;
      })}
      <path d={areaD} fill="url(#dbRevGrad)"/>
      <path d={pathD} fill="none" stroke="#F5A623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {pts.map((p,i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3.5" fill="#F5A623"/>
          <circle cx={p.x} cy={p.y} r="1.5" fill="#fff"/>
          <text x={p.x} y={H-6} textAnchor="middle" fontSize="8.5" fill="currentColor" opacity="0.35">{months[i]}</text>
        </g>
      ))}
    </svg>
  );
}

const KPI_DETAILS = {
  revenue: { title:'Revenue this month', color:'#F5A623', rows:[{label:'Collected',val:'₹1.8L',note:'2 paid'},{label:'Pending',val:'₹2.4L',note:'4 invoices'},{label:'Overdue',val:'₹35K',note:'1 invoice'},{label:'Target',val:'₹3L',note:'80% done'}], note:'View Finance →', path:'/finance' },
  leads:   { title:'Active leads', color:'#22C55E', rows:[{label:'New',val:'12',note:''},{label:'Contacted',val:'9',note:''},{label:'Warm',val:'10',note:''},{label:'Hot',val:'7',note:'follow up now'},{label:'Closed',val:'6',note:'this month'},{label:'Lost',val:'4',note:''}], note:'View Leads →', path:'/leads' },
  tasks:   { title:'Tasks done', color:'#3B82F6', rows:[{label:'Completed',val:'47',note:''},{label:'Overdue',val:'2',note:'action needed'},{label:'In progress',val:'8',note:''},{label:'Not started',val:'3',note:''}], note:'View Projects →', path:'/projects' },
  team:    { title:'Team present', color:'#8B5CF6', rows:[{label:'Present today',val:'9',note:''},{label:'On leave',val:'3',note:'approved'},{label:'Absent',val:'0',note:''},{label:'Total members',val:'12',note:''}], note:'View Team →', path:'/team' },
};

function KPIModal({ data, theme, onClose, navigate }) {
  return (
    <div className="db-kpi-overlay" onClick={onClose}>
      <div className="db-kpi-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="db-kpi-hd">
          <div className="db-kpi-title" style={{color:data.color}}>{data.title}</div>
          <div className="db-kpi-close" onClick={onClose}>✕</div>
        </div>
        <div className="db-kpi-rows">
          {data.rows.map(r=>(
            <div key={r.label} className="db-kpi-row">
              <span className="db-kpi-row-lbl">{r.label}</span>
              <div className="db-kpi-row-right">
                <span className="db-kpi-row-val">{r.val}</span>
                {r.note && <span className="db-kpi-row-note">{r.note}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="db-kpi-link" onClick={()=>{navigate(data.path);onClose();}}>{data.note}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { theme } = useTheme();
  const navigate  = useNavigate();
  const today     = new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  const [kpiModal, setKpiModal] = useState(null);

  const [showFollowUps, setShowFollowUps] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const allLeads = (() => { try { return JSON.parse(localStorage.getItem('ryzenos_leads')||'[]'); } catch { return []; } })();
  const dueTodayLeads = allLeads.filter(l => l.followUp === todayStr);
  const overdueLeads = allLeads.filter(l => l.followUp && l.followUp < todayStr);
  const closedLeads = allLeads.filter(l => l.status === 'closed');
  const totalDue = dueTodayLeads.length + overdueLeads.length;

  const revenueData  = [180000, 210000, 195000, 240000, 258000, 240000];
  const STAT_CARDS = [
    { key:'revenue', lbl:'Revenue this month', val:'₹2.4L',  chg:'18% vs last month', cls:'db-up',   color:'#F5A623', spark:[18,21,19.5,24,25.8,24] },
    { key:'leads',   lbl:'Active leads',       val:'48',     chg:'6 new today',        cls:'db-up',   color:'#22C55E', spark:[30,35,38,42,46,48] },
    { key:'tasks',   lbl:'Tasks done',         val:'94%',    chg:'2 overdue',          cls:'db-warn', color:'#3B82F6', spark:[80,85,88,90,92,94] },
    { key:'team',    lbl:'Team present',       val:'9/12',   chg:'3 on leave',         cls:'db-dn',   color:'#8B5CF6', spark:[11,10,9,12,10,9] },
  ];

  const LEAD_SOURCES = [
    { label:'Meta Ads', val:38, color:'#F5A623' },
    { label:'BNI Referral', val:28, color:'#3B82F6' },
    { label:'Website', val:20, color:'#22C55E' },
    { label:'Direct', val:10, color:'#8B5CF6' },
    { label:'Other', val:4, color:'#6B7280' },
  ];
  const maxSource = Math.max(...LEAD_SOURCES.map(s=>s.val));

  const PROJECTS = [
    { name:'LIC Portal',         status:'In Progress', progress:65, due:'No deadline', color:'#F5A623' },
    { name:'Permainvest',        status:'In Progress', progress:40, due:'No deadline', color:'#3B82F6' },
    { name:'Auxin Shipping CRM', status:'In Review',   progress:85, due:'15 Jun',      color:'#22C55E' },
  ];

  const TOP_CLIENTS = [
    { name:'Auxin Shipping',   val:250000 },
    { name:'Elaam Bazaar',     val:610000 },
    { name:'Rao Infra',        val:120000 },
    { name:'Jyothi Elaam',     val:53000  },
  ];
  const maxClient = Math.max(...TOP_CLIENTS.map(c=>c.val));

  const LEADS = [
    { name:'Anand Kumar',  src:'Meta Ads',  badge:'Hot',  cls:'db-b-gold' },
    { name:'Priya Sharma', src:'Website',   badge:'New',  cls:'db-b-green' },
    { name:'Rajesh Nair',  src:'Referral',  badge:'Warm', cls:'db-b-blue' },
    { name:'Meera Pillai', src:'Meta Ads',  badge:'New',  cls:'db-b-green' },
  ];

  const AI_INSIGHTS = [
    <><strong>Anand Kumar</strong> hasn't been followed up in 3 days.</>,
    <>Revenue on track to hit <strong>₹3L</strong> if 4 warm leads close.</>,
    <><strong>2 employees</strong> haven't marked attendance today.</>,
    <>Next billing in <strong>30 days</strong> — ₹7,499.</>,
  ];

  return (
    <div className="db-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="db-main">
        <Topbar title={<>Good morning, <span>Dhanush</span></>}/>
        <div className="db-content">

          {/* Today strip */}
          <div className="db-today" onClick={()=>setShowFollowUps(true)} style={{cursor:'pointer'}}>
            <div><div className="db-today-sub">{today}</div><div className="db-today-title">{totalDue > 0 ? `${totalDue} follow-up${totalDue>1?'s':''} due` : 'No follow-ups due today'}</div></div>
            <div className="db-chips">
              {dueTodayLeads.length > 0 && <div className="db-chip db-chip-gold">{dueTodayLeads.length} today</div>}
              {closedLeads.length > 0 && <div className="db-chip db-chip-green">{closedLeads.length} closed</div>}
              {overdueLeads.length > 0 && <div className="db-chip db-chip-red">{overdueLeads.length} overdue</div>}
              {totalDue === 0 && <div className="db-chip db-chip-green">All clear</div>}
            </div>
          </div>

          {showFollowUps && (
            <div className="db-kpi-overlay" onClick={()=>setShowFollowUps(false)}>
              <div className="db-kpi-modal" data-theme={theme} onClick={e=>e.stopPropagation()} style={{width:420}}>
                <div className="db-kpi-hd">
                  <div className="db-kpi-title" style={{color:'#F5A623'}}>Follow-ups due today</div>
                  <div className="db-kpi-close" onClick={()=>setShowFollowUps(false)}>✕</div>
                </div>
                <div className="db-kpi-rows" style={{maxHeight:360,overflowY:'auto'}}>
                  {dueTodayLeads.length === 0 ? (
                    <div style={{padding:'16px 0',textAlign:'center',fontSize:13,opacity:.5}}>No follow-ups due today</div>
                  ) : dueTodayLeads.map(l=>(
                    <div key={l.id} className="db-kpi-row">
                      <div>
                        <div style={{fontSize:13,fontWeight:600}}>{l.name}</div>
                        <div style={{fontSize:11,opacity:.5,marginTop:2}}>{l.source} · {l.status}</div>
                      </div>
                      <div className="db-kpi-row-right">
                        {l.value>0 && <span className="db-kpi-row-val">{fmtINR(l.value)}</span>}
                        <span className="db-kpi-row-note">{l.assigned}</span>
                      </div>
                    </div>
                  ))}
                  {overdueLeads.length > 0 && <>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',opacity:.4,padding:'12px 0 4px'}}>Overdue</div>
                    {overdueLeads.map(l=>(
                      <div key={l.id} className="db-kpi-row">
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:'#ef4444'}}>{l.name}</div>
                          <div style={{fontSize:11,opacity:.5,marginTop:2}}>{l.followUp} · {l.source}</div>
                        </div>
                        <span className="db-kpi-row-note" style={{color:'#ef4444'}}>{l.assigned}</span>
                      </div>
                    ))}
                  </>}
                </div>
                <div className="db-kpi-link" onClick={()=>{navigate('/leads');setShowFollowUps(false);}}>View all leads →</div>
              </div>
            </div>
          )}

          {/* Stat cards */}
          <div className="db-stats">
            {STAT_CARDS.map(s => (
              <div key={s.key} className="db-stat db-stat-clickable" onClick={()=>setKpiModal(s.key)}>
                <div className="db-stat-lbl">{s.lbl}</div>
                <div className="db-stat-val" style={s.key==='revenue'?{color:'#F5A623'}:{}}>{s.val}</div>
                <div className={`db-stat-chg ${s.cls}`}>{s.key==='revenue'?'↑ ':s.key==='leads'?'↑ ':''}{s.chg}</div>
                <Spark data={s.spark} color={s.color}/>
              </div>
            ))}
          </div>

          {/* Revenue chart + Lead sources */}
          <div className="db-chart-row">
            <div className="db-panel db-panel-wide">
              <div className="db-panel-hd">
                <span className="db-panel-title">Revenue trend — 2026</span>
                <span className="db-panel-tag" style={{color:'#F5A623'}}>Jun: ₹2.4L</span>
              </div>
              <RevenueChart data={revenueData}/>
            </div>
            <div className="db-panel">
              <div className="db-panel-hd"><span className="db-panel-title">Lead sources</span></div>
              <div className="db-source-list">
                {LEAD_SOURCES.map(s=>(
                  <div key={s.label} className="db-source-row">
                    <div className="db-source-dot" style={{background:s.color}}/>
                    <div className="db-source-lbl">{s.label}</div>
                    <div className="db-source-bar-wrap">
                      <div className="db-source-bar-fill" style={{width:(s.val/maxSource*100)+'%',background:s.color}}/>
                    </div>
                    <div className="db-source-pct" style={{color:s.color}}>{s.val}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* KPI summary row */}
          <div className="db-kpi-row-4">
            {[
              {lbl:'Total Clients',   val:17,    sub:'across all ventures',  color:'#F5A623', path:'/team'},
              {lbl:'Total Projects',  val:5,     sub:'3 active',             color:'#3B82F6', path:'/projects'},
              {lbl:'Open Leads',      val:11,    sub:'in pipeline',          color:'#22C55E', path:'/leads'},
              {lbl:'Last Month Rev',  val:'₹2.1L',sub:'May 2026',           color:'#8B5CF6', path:'/finance'},
            ].map(k=>(
              <div key={k.lbl} className="db-kpi4-card" onClick={()=>navigate(k.path)}>
                <div className="db-kpi4-lbl">{k.lbl}</div>
                <div className="db-kpi4-val" style={{color:k.color}}>{k.val}</div>
                <div className="db-kpi4-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Projects + Top clients */}
          <div className="db-bot">
            <div className="db-panel">
              <div className="db-panel-hd">
                <span className="db-panel-title">Live project status</span>
                <span className="db-panel-link" onClick={()=>navigate('/projects')}>View all</span>
              </div>
              {PROJECTS.map(p=>(
                <div key={p.name} className="db-pj-row">
                  <div className="db-pj-left">
                    <div className="db-pj-dot" style={{background:p.color}}/>
                    <div>
                      <div className="db-pj-name">{p.name}</div>
                      <div className="db-pj-status">{p.status}</div>
                    </div>
                  </div>
                  <div className="db-pj-right">
                    <div className="db-pj-bar-wrap"><div className="db-pj-bar-fill" style={{width:p.progress+'%',background:p.color}}/></div>
                    <div className="db-pj-due">{p.due}</div>
                  </div>
                </div>
              ))}
              <div className="db-all-ok"><span style={{color:'#22C55E'}}>✓</span> All projects on track</div>
            </div>
            <div className="db-panel">
              <div className="db-panel-hd"><span className="db-panel-title">Top clients by value</span></div>
              {TOP_CLIENTS.map(c=>(
                <div key={c.name} className="db-client-row">
                  <div className="db-client-name">{c.name}</div>
                  <div className="db-client-bar-wrap"><div className="db-client-bar-fill" style={{width:(c.val/maxClient*100)+'%'}}/></div>
                  <div className="db-client-val">{fmtINR(c.val)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent leads + AI */}
          <div className="db-mid">
            <div className="db-panel">
              <div className="db-panel-hd"><span className="db-panel-title">Recent leads</span><span className="db-panel-link" onClick={()=>navigate('/leads')}>View all</span></div>
              {LEADS.map(l=>(
                <div key={l.name} className="db-lead-row">
                  <div><div className="db-lead-name">{l.name}</div><div className="db-lead-src">{l.src}</div></div>
                  <span className={`db-badge ${l.cls}`}>{l.badge}</span>
                </div>
              ))}
            </div>
            <div className="db-ai">
              <div className="db-ai-hd"><div className="db-ai-dot"/><span className="db-ai-title">Ryzen AI</span></div>
              {AI_INSIGHTS.map((a,i)=><div key={i} className="db-ai-item">{a}</div>)}
            </div>
          </div>

        </div>
      </div>
      {kpiModal && <KPIModal data={KPI_DETAILS[kpiModal]} theme={theme} onClose={()=>setKpiModal(null)} navigate={navigate}/>}
    </div>
  );
}
